import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import { fetchGet } from '../../../../helpers/fetching';
import {
  setActionToConfirm,
  setSnackbar,
  setActiveDialog,
  sendStructuredReport,
  sendQualityReport,
  sendDiagnosticsReport,
  sendScoreCardReport,
  sendFHIRReport,
  sendHL7Report,
} from '../../../../store';
import { useTranslation } from 'react-i18next';
import animation from '../../../../resources/processing_animation.gif';
import { Divider, Tab, Tabs } from '@material-ui/core';

const Report = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const objectUrlsRef = useRef(new Set());

  const {
    confirmed_density,
    confirmed_quality,
    confirmed_opacities,
    confirmed_microcalc,
    pat_name,
    report_sending_rules,
    hl7_sending_rules,
    fhir_sending_rules,
  } = useSelector((state) => state.results);
  const currentExam = useSelector((state) => state.examinations.currentExam);
  const { premium_features } = useSelector((state) => state.global_settings);
  const landscape = useSelector((state) => state.gui.landscape);

  const [qualityPdfSrc, setQualityPdfSrc] = useState(null);
  const [diagnosticsPages, setDiagnosticsPages] = useState([]);
  const [diagnosticsIsPdf, setDiagnosticsIsPdf] = useState(true);
  const [scoreCardSrc, setScoreCardSrc] = useState(null);
  const [reportViewTab, setReportViewTab] = useState(0);
  const [loadingReport, setLoadingReport] = useState(null);

  const loggedUser = localStorage.getItem('username');
  const { diagnostics_microcalc_module, diagnostics_opacities_module } = premium_features;

  const {
    quality_report_manual,
    diagnostics_report_manual,
    structured_report_manual,
    score_card_manual,
    diagnostics_report_direct,
    quality_report_direct,
    structured_report_direct,
    score_card_direct,
  } = report_sending_rules || {};
  const { fhir_manual, fhir_direct } = fhir_sending_rules || {};
  const { hl7_manual, hl7_direct } = hl7_sending_rules || {};

  const noRuleDefined = [
    quality_report_manual,
    diagnostics_report_manual,
    structured_report_manual,
    score_card_manual,
    diagnostics_report_direct,
    quality_report_direct,
    structured_report_direct,
    score_card_direct,
  ].every((value) => value == null || value === 0);

  const somePremiumDiagnostics =
    (diagnostics_microcalc_module || diagnostics_opacities_module) &&
    localStorage.getItem('diagnostics') === 'true';

  const revokeDiagnosticsPages = useCallback((pages) => {
    pages.forEach(({ url }) => {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
      }
    });
  }, []);

  const fetchReportBlob = async (route, abortSignal) => {
    const response = await fetchGet(route, 'bbox', false, 'blob', abortSignal);
    if (response?.aborted) {
      return { aborted: true };
    }
    const responseType = response?.contentType || response?.blob?.type || '';
    const isValid =
      response?.ok &&
      response?.blob &&
      ['image/', 'application/pdf'].some((prefix) => responseType.startsWith(prefix));
    return { response, responseType, isValid };
  };

  const fetchReports = useCallback(
    async (abortSignal) => {
      setLoadingReport(t('report.generating'));
      let hasError = false;

      try {
        const loadSingleReport = async (type, setter) => {
          const query = new URLSearchParams({ report_type: type });
          try {
            const { aborted, isValid, response, responseType } = await fetchReportBlob(
              `report/${currentExam}?${query.toString()}`,
              abortSignal,
            );
            if (aborted) {
              return 'aborted';
            }
            if (isValid) {
              const nextUrl = URL.createObjectURL(response.blob);
              objectUrlsRef.current.add(nextUrl);
              setter((prev) => {
                if (prev?.startsWith('blob:')) {
                  URL.revokeObjectURL(prev);
                  objectUrlsRef.current.delete(prev);
                }
                return nextUrl;
              });
              return 'ok';
            }
            console.warn('Report fetch failed or invalid content type', {
              reportType: type,
              status: response?.status,
              contentType: responseType,
            });
          } catch (error) {
            if (error?.name === 'AbortError') {
              return 'aborted';
            }
            console.warn('Report fetch crashed', { reportType: type, error });
          }
          setter((prev) => {
            if (prev?.startsWith('blob:')) {
              URL.revokeObjectURL(prev);
              objectUrlsRef.current.delete(prev);
            }
            return null;
          });
          return 'error';
        };

        const scoreStatus = await loadSingleReport('score_card', setScoreCardSrc);
        if (scoreStatus === 'aborted') {
          return;
        }
        if (scoreStatus === 'error') {
          hasError = true;
        }

        const qualityStatus = await loadSingleReport('quality', setQualityPdfSrc);
        if (qualityStatus === 'aborted') {
          return;
        }
        if (qualityStatus === 'error') {
          hasError = true;
        }

        if (somePremiumDiagnostics) {
          try {
            const manifestQuery = new URLSearchParams({
              report_type: 'diagnostics',
              preview_manifest: '1',
            });
            const manifest = await fetchGet(
              `report/${currentExam}?${manifestQuery.toString()}`,
              'bbox',
              false,
              'json',
              abortSignal,
            );
            if (manifest?.aborted) {
              return;
            }

            const pages = manifest?.pages;
            if (!Array.isArray(pages) || pages.length === 0) {
              console.warn('Diagnostics manifest invalid', { manifest });
              setDiagnosticsPages((prev) => {
                revokeDiagnosticsPages(prev);
                return [];
              });
              hasError = true;
            } else {
              setDiagnosticsIsPdf(manifest.format === 'pdf');
              const loadedPages = [];

              for (const { label, page } of pages) {
                const pageQuery = new URLSearchParams({
                  report_type: 'diagnostics',
                  preview_page: page,
                });
                const { aborted, isValid, response } = await fetchReportBlob(
                  `report/${currentExam}?${pageQuery.toString()}`,
                  abortSignal,
                );
                if (aborted) {
                  return;
                }
                if (!isValid) {
                  hasError = true;
                  continue;
                }
                const url = URL.createObjectURL(response.blob);
                objectUrlsRef.current.add(url);
                loadedPages.push({ label, url });
              }

              setDiagnosticsPages((prev) => {
                revokeDiagnosticsPages(prev);
                return loadedPages;
              });
              if (!loadedPages.length) {
                hasError = true;
              }
            }
          } catch (error) {
            if (error?.name === 'AbortError') {
              return;
            }
            setDiagnosticsPages((prev) => {
              revokeDiagnosticsPages(prev);
              return [];
            });
            hasError = true;
          }
        }
      } finally {
        setLoadingReport(null);
        if (hasError) {
          dispatch(
            setSnackbar({
              msg: 'report.failed_code',
              severity: 'warning',
            }),
          );
        }
      }
    },
    [currentExam, dispatch, revokeDiagnosticsPages, somePremiumDiagnostics, t],
  );

  const onScoreCardSend = () => {
    if (
      ![confirmed_density, confirmed_quality, confirmed_opacities, confirmed_microcalc].every((item) => item)
    )
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendScoreCardReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.sending_score_card'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendScoreCardReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.sending_score_card'),
          severity: 'info',
        }),
      );
    }
  };

  const onSRSend = () => {
    if (![confirmed_density, confirmed_quality, confirmed_opacities].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendStructuredReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_structure_pacs'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendStructuredReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_structure_pacs'),
          severity: 'info',
        }),
      );
    }
  };

  const onQualityReportSend = () => {
    if (![confirmed_density, confirmed_quality].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendQualityReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_img'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendQualityReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_img'),
          severity: 'info',
        }),
      );
    }
  };

  const onDiagnoseReportSend = () => {
    if (![confirmed_density, confirmed_opacities, confirmed_microcalc].every((item) => item))
      dispatch(
        setActionToConfirm({
          action: () => {
            dispatch(sendDiagnosticsReport(currentExam, loggedUser, t));
            dispatch(
              setSnackbar({
                msg: t('report.send_img'),
                severity: 'info',
              }),
            );
          },
          text: t('report.not_confirmed_send'),
        }),
      );
    else {
      dispatch(sendDiagnosticsReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_img'),
          severity: 'info',
        }),
      );
    }
  };

  const onFHIRSend = () => {
    if (
      ![confirmed_density, confirmed_quality, confirmed_opacities, confirmed_microcalc].every((item) => item)
    ) {
      dispatch(sendFHIRReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_fhir'),
          severity: 'info',
        }),
      );
    } else {
      dispatch(sendFHIRReport(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_fhir'),
          severity: 'info',
        }),
      );
    }
  };

  const onHL7Send = () => {
    if (
      ![confirmed_density, confirmed_quality, confirmed_opacities, confirmed_microcalc].every((item) => item)
    ) {
      dispatch(sendHL7Report(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_hl7'),
          severity: 'info',
        }),
      );
    } else {
      dispatch(sendHL7Report(currentExam, loggedUser, t));
      dispatch(
        setSnackbar({
          msg: t('report.send_hl7'),
          severity: 'info',
        }),
      );
    }
  };

  useEffect(() => {
    const abortReports = new AbortController();
    fetchReports(abortReports.signal);
    return () => abortReports.abort();
  }, [fetchReports]);

  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  return (
    <DraggableDialog
      maxWidth='xl'
      title={t('ResultView.report_title', { user: pat_name })}
      open={props.open}
      customStyle={{ overflow: 'auto' }}
      actions={[
        !landscape && {
          onClick: () => dispatch(setActiveDialog(null)),
          color: 'secondary',
          label: t('Console.close'),
        },
        somePremiumDiagnostics &&
          (diagnostics_report_manual === 1 || diagnostics_report_direct === 1) && {
            onClick: onDiagnoseReportSend,
            color: 'primary',
            label: t('report.send_diagnose'),
            disabled: !!loadingReport,
          },
        (quality_report_manual === 1 || quality_report_direct === 1) && {
          onClick: onQualityReportSend,
          color: 'primary',
          label: t('report.send_quality'),
          disabled: !!loadingReport,
        },
        (structured_report_manual === 1 || structured_report_direct === 1) && {
          onClick: onSRSend,
          color: 'primary',
          label: t('report.send_sr'),
          disabled: !!loadingReport,
        },
        (score_card_manual === 1 || score_card_direct === 1) && {
          onClick: onScoreCardSend,
          color: 'primary',
          label: t('report.send_score_card'),
          disabled: !!loadingReport,
        },
        (fhir_manual === 1 || fhir_direct === 1) && {
          onClick: onFHIRSend,
          color: 'primary',
          label: t('report.send_fhir'),
          disabled: !!loadingReport,
        },
        (hl7_manual === 1 || hl7_direct === 1) && {
          onClick: onHL7Send,
          color: 'primary',
          label: t('report.send_hl7'),
          disabled: !!loadingReport,
        },
        noRuleDefined && {
          onClick: () => dispatch(setActiveDialog('config')),
          color: 'primary',
          label: t('report.set_rules'),
          disabled: !!loadingReport,
        },
      ]}
      onClose={() => dispatch(setActiveDialog(null))}>
      {loadingReport ? (
        <>
          <img
            style={{ width: '100px', height: '100px', borderRadius: '50%' }}
            src={animation}
            alt='logoanimation'
          />
          <p>{loadingReport}</p>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <Tabs
            value={reportViewTab}
            onChange={(_, tabValue) => setReportViewTab(tabValue)}
            indicatorColor='primary'
            textColor='primary'
            centered>
            <Tab label='Score Card' />
            <Tab label='Quality' />
            {somePremiumDiagnostics && <Tab label='Diagnostics' />}{' '}
          </Tabs>

          <Divider orientation='horizontal' variant='fullWidth' style={{ margin: '10px 0' }} />

          {reportViewTab === 0 &&
            (scoreCardSrc ? (
              <img
                src={scoreCardSrc}
                alt={t('report.score_card_unavailable')}
                style={{ maxWidth: '90vw', maxHeight: '70vh', objectFit: 'contain' }}
              />
            ) : (
              <p>{t('report.score_card_unavailable')}</p>
            ))}

          {reportViewTab === 1 &&
            (qualityPdfSrc ? (
              <iframe
                src={qualityPdfSrc}
                title='quality-report-pdf'
                style={{ width: '90vw', height: '80vh', border: 'none' }}
              />
            ) : (
              <p>{t('report.quality_unavailable')}</p>
            ))}

          {reportViewTab === 2 &&
            (diagnosticsPages.length ? (
              <div style={{ maxHeight: '80vh', overflowY: 'auto', width: '90vw' }}>
                {diagnosticsPages.map(({ label, url }) =>
                  diagnosticsIsPdf ? (
                    <iframe
                      key={label}
                      src={url}
                      title={`diagnostics-${label}`}
                      style={{
                        width: '100%',
                        height: '80vh',
                        border: 'none',
                        marginBottom: 16,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <img
                      key={label}
                      src={url}
                      alt={label}
                      style={{
                        width: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        marginBottom: 16,
                        display: 'block',
                      }}
                    />
                  ),
                )}
              </div>
            ) : (
              <p>{t('report.diagnostics_unavailable')}</p>
            ))}
        </div>
      )}
    </DraggableDialog>
  );
};

export default Report;
